<template>
  <Section
    :id="id"
    :header="
      t('common.relatedParam', {
        param: t('referenceData.referenceNumbers', 2),
      })
    "
    :loading="loading || isAnySaving"
    :class="{ expanded: isExpanded }"
    class="related-ref-nums-section"
  >
    <template #headerRight>
      <ExpandButton
        v-model="isExpanded"
        target-id="related-ref-nums-data"
        no-labels
      />
    </template>

    <div v-show="isExpanded" id="related-ref-nums-data" tabindex="-1">
      <p v-if="!groupedOrders.length">
        {{
          t('common.noParam', {
            param: t('common.relatedParam', {
              param: t('referenceData.referenceNumbers', 2),
            }),
          })
        }}
      </p>

      <div v-for="order in groupedOrders" :key="order.resourceId">
        <h3 class="section-subheader">
          {{ t('orders.orderNumParam', { param: order.resourceId }) }}
        </h3>
        <ReferenceNumberViewEdit
          v-for="(referenceNum, index) in order.referenceNumbers"
          :id="`${id}-${referenceNum.typeId}`"
          :key="index"
          :model-value="referenceNum"
          :client-types="availableClientReferenceNumberTypes.data"
          :editable="editable"
          @update:model-value="
            onReferenceNumberUpdate(order.orderIndex, index, $event)
          "
          @delete="onReferenceNumberDelete(order.orderIndex, index)"
        />
      </div>
    </div>
  </Section>
</template>

<script lang="ts" setup>
import { useLocalStorage } from '@vueuse/core';
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  usePartitionedStorageKey,
  useToast,
} from '@rygen/rygen-ui/composables';
import {
  DomainReference,
  Loadable,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useReferenceNumberTypes } from '@/composables';
import { Order, ReferenceNumber } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useParentEntityStore } from '@/stores/ParentEntity';

import ReferenceNumberViewEdit from '@/components/referenceNumber/ReferenceNumberViewEdit.vue';
import ExpandButton from '@rygen/rygen-ui/components/button/ExpandButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  orders: {
    type: Object as PropType<Loadable<Order[]>>,
    default: () => ({ loading: false }),
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, orders, loading, editable } = toRefs(props);

const { currentUser, selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const { owningClient } = useParentEntityStore();
const { storageKey: isExpandedKey } = usePartitionedStorageKey(
  'related-ref-nums-expanded',
  {
    currentUser,
    selectedDomain,
  },
);
const isExpanded = useLocalStorage(isExpandedKey.value, true);
const { availableClientReferenceNumberTypes, loadReferenceNumberTypes } =
  useReferenceNumberTypes(toRef(() => [ReferenceNumberTypeLevel.ORDER]));
const toast = useToast();

const isSaving: Ref<boolean[]> = ref([]);

const isAnySaving = computed(() => {
  return isSaving.value.some((saving) => saving);
});

const groupedOrders = computed(() => {
  return (orders.value.data ?? []).map((order: Order, index: number) => ({
    orderIndex: index,
    resourceId: String(order.id),
    referenceNumbers: order.referenceNumbers,
  }));
});

const onReferenceNumberUpdate = async (
  orderIndex: number,
  index: number,
  updatedReferenceNumber: ReferenceNumber,
) => {
  const orderData = orders.value.data ?? [];
  const order = orderData[orderIndex];
  const refNumToUpdate = order.referenceNumbers[index];
  if (!refNumToUpdate) {
    toast.addError({
      detail: `Reference number not found`,
      error: new Error(`Reference number not found`),
    });
    return;
  }

  Object.assign(refNumToUpdate, updatedReferenceNumber);

  try {
    isSaving.value[orderIndex] = true;
    const updatedOrder = await api.order.update(order);
    orderData.splice(orderIndex, 1, updatedOrder);
    toast.addSuccess({
      detail: t('messages.successUpdatingParam', {
        param: t('orders.orderNumParam', { param: order.id }),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving related reference numbers', e);
    toast.addError({
      detail: t('errors.savingParam', {
        param: t('common.relatedParam', {
          param: t('referenceData.referenceNumbers', 2),
        }),
      }),
      error: e,
    });
  } finally {
    isSaving.value[orderIndex] = false;
  }
};

const onReferenceNumberDelete = async (orderIndex: number, index: number) => {
  const orderData = orders.value.data ?? [];
  const order = orderData[orderIndex];
  const refNumToDelete = order.referenceNumbers[index];
  if (!refNumToDelete) {
    toast.addError({
      detail: `Reference number not found`,
      error: new Error(`Reference number not found`),
    });
    return;
  }

  try {
    isSaving.value[orderIndex] = true;

    order.referenceNumbers.splice(index, 1);

    const updatedOrder = await api.order.update(order);
    orderData.splice(orderIndex, 1, updatedOrder);
    toast.addSuccess({
      detail: t('messages.successUpdatingParam', {
        param: t('orders.orderNumParam', { param: order.id }),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error deleting related reference number', e);
    toast.addError({
      detail: t('errors.savingParam', {
        param: t('common.relatedParam', {
          param: t('referenceData.referenceNumbers', 2),
        }),
      }),
      error: e,
    });
    order.referenceNumbers.splice(index, 0, refNumToDelete);
  } finally {
    isSaving.value[orderIndex] = false;
  }
};

watch(
  owningClient,
  (newVal: DomainReference | undefined) => {
    if (newVal) {
      loadReferenceNumberTypes({ level: ReferenceNumberTypeLevel.ORDER });
    }
  },
  { immediate: true },
);
</script>

<style>
.related-ref-nums-section {
  &:not(.expanded) {
    --section-header-margin: 0;
  }
}
</style>
